import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Link as LinkIcon,
  Cog,
  Database,
  Workflow,
  LayoutDashboard,
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';

export default function Services() {
  const { language } = useLanguage();

  const services = [
    {
      icon: LinkIcon,
      title: language === 'pt' ? 'Integrações entre plataformas' : 'Platform integrations',
      description:
        language === 'pt'
          ? 'Conectamos loja, atendimento, CRM, ERP, planilhas, gateways e APIs para reduzir retrabalho e erros operacionais.'
          : 'We connect store, support, CRM, ERP, spreadsheets, gateways, and APIs to reduce rework and operational errors.',
      forWho:
        language === 'pt'
          ? 'Para operações que já cresceram o suficiente para sentir dor de sistema desconectado.'
          : 'For operations that have grown enough to feel the pain of disconnected systems.',
      color: 'cyan',
    },
    {
      icon: Cog,
      title: language === 'pt' ? 'Automação operacional' : 'Operational automation',
      description:
        language === 'pt'
          ? 'Automatizamos rotinas manuais do dia a dia para liberar tempo do time e dar mais consistência aos processos.'
          : 'We automate everyday manual routines to free up team time and make processes more consistent.',
      forWho:
        language === 'pt'
          ? 'Para empresas pequenas ou médias que já estão sentindo o peso do operacional.'
          : 'For small and mid-sized businesses already feeling the weight of operations.',
      color: 'purple',
    },
    {
      icon: Workflow,
      title: language === 'pt' ? 'Fluxos sob medida' : 'Custom workflows',
      description:
        language === 'pt'
          ? 'Desenhamos fluxos específicos para vendas, atendimento, pós-venda, financeiro e outras rotinas que não cabem em soluções prontas.'
          : 'We design specific flows for sales, support, after-sales, finance, and other routines that do not fit boxed solutions.',
      forWho:
        language === 'pt'
          ? 'Para quando o negócio precisa de algo mais específico do que “instalar um app”.'
          : 'For when the business needs something more specific than “installing an app”.',
      color: 'emerald',
    },
    {
      icon: LayoutDashboard,
      title: language === 'pt' ? 'Dados, métricas e dashboards' : 'Data, metrics, and dashboards',
      description:
        language === 'pt'
          ? 'Transformamos dados soltos em visibilidade prática: dashboards, relatórios e indicadores que ajudam a decidir melhor.'
          : 'We turn scattered data into practical visibility: dashboards, reports, and indicators that support better decisions.',
      forWho:
        language === 'pt'
          ? 'Para quem precisa crescer com mais clareza e menos achismo.'
          : 'For teams that need to grow with more clarity and less guesswork.',
      color: 'amber',
    },
  ];

  const getColorClasses = (color) => {
    const colors = {
      cyan: 'bg-cyan-500/10 text-cyan-400 border-cyan-500/30',
      purple: 'bg-purple-500/10 text-purple-400 border-purple-500/30',
      emerald: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30',
      amber: 'bg-amber-500/10 text-amber-400 border-amber-500/30',
    };
    return colors[color] || colors.cyan;
  };

  return (
    <div className="relative">
      <section className="relative py-20 lg:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-slate-900/50 to-slate-950" />
        <div className="absolute top-1/4 left-0 w-[600px] h-[600px] bg-purple-500/10 rounded-full blur-[120px] pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="font-outfit text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-100 leading-tight tracking-tight">
              {language === 'pt' ? 'Projetos especiais para quando o básico já estiver rodando' : 'Special projects for when the basics are already running'}
            </h1>
            <p className="mt-6 text-lg lg:text-xl text-slate-400 leading-relaxed">
              {language === 'pt'
                ? 'Esses serviços entram quando a operação pede mais do que uma loja pronta ou uma automação pontual. É aqui que a ProcessSurge aprofunda a engenharia.'
                : 'These services come in when the operation needs more than a ready-made store or a single automation. This is where ProcessSurge deepens the engineering.'}
            </p>
          </div>
        </div>
      </section>

      <section className="py-16 lg:py-24 bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <div
                key={index}
                className="bg-slate-900/50 border border-slate-800 rounded-3xl p-8 lg:p-10 hover:border-slate-700 transition-colors group"
              >
                <div className={`w-14 h-14 ${getColorClasses(service.color).split(' ')[0]} rounded-2xl flex items-center justify-center mb-6`}>
                  <service.icon className={`w-7 h-7 ${getColorClasses(service.color).split(' ')[1]}`} />
                </div>

                <h3 className="font-outfit text-2xl font-bold text-slate-100 mb-4 group-hover:text-cyan-400 transition-colors">
                  {service.title}
                </h3>

                <p className="text-slate-300 leading-relaxed mb-6">{service.description}</p>

                <div className="flex items-center gap-3 pt-6 border-t border-slate-800/50">
                  <div className="w-1.5 h-1.5 rounded-full bg-slate-500" />
                  <p className="text-sm text-slate-500 font-medium uppercase tracking-wide">{service.forWho}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-32 bg-slate-900/30 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-[600px] h-[400px] bg-purple-500/10 rounded-full blur-[100px] pointer-events-none" />

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-outfit text-3xl sm:text-4xl font-bold text-slate-100 tracking-tight">
            {language === 'pt' ? 'Tem um gargalo específico fora do padrão?' : 'Have a specific bottleneck outside the standard flow?'}
          </h2>
          <p className="mt-4 text-slate-400 max-w-2xl mx-auto">
            {language === 'pt'
              ? 'A ProcessSurge também atua como braço técnico para montar fluxos, conectar ferramentas e dar previsibilidade a operações que cresceram rápido demais.'
              : 'ProcessSurge also acts as a technical arm to build flows, connect tools, and bring predictability to operations that grew too fast.'}
          </p>
          <div className="mt-8">
            <Link to={`/${language}/contact`}>
              <Button className="bg-purple-600 text-white hover:bg-purple-500 font-bold rounded-full px-10 py-6 text-lg transition-transform hover:scale-105">
                {language === 'pt' ? 'Trazer meu desafio' : 'Bring my challenge'}
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
